
import { useEffect, useRef, useState } from 'react';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const projects = [
    {
      title: 'Regular Calf Monitoring System',
      description: 'Implemented comprehensive calf monitoring protocols to reduce stress-related behaviors and improve animal welfare standards.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20calf%20monitoring%20in%20modern%20farm%20setting%2C%20animal%20welfare%2C%20agricultural%20technology%2C%20livestock%20management%2C%20veterinary%20care%2C%20sustainable%20farming%20practices%2C%20clean%20farm%20environment&width=400&height=300&seq=project1&orientation=landscape',
      technologies: ['Animal Welfare', 'Monitoring Systems', 'Data Analysis'],
      impact: 'Reduced stress-related behaviors by 40%'
    },
    {
      title: 'Soil Testing & Enhancement Program',
      description: 'Developed and executed comprehensive soil testing protocols to enhance crop productivity through targeted nutrient management.',
      image: 'https://readdy.ai/api/search-image?query=Soil%20testing%20in%20agricultural%20field%2C%20laboratory%20analysis%2C%20crop%20productivity%20enhancement%2C%20sustainable%20farming%2C%20agricultural%20science%2C%20soil%20samples%2C%20modern%20farming%20techniques&width=400&height=300&seq=project2&orientation=landscape',
      technologies: ['Soil Science', 'Laboratory Analysis', 'Crop Management'],
      impact: 'Increased crop productivity by 35%'
    },
    {
      title: 'Optimized Grazing Systems',
      description: 'Designed and implemented strategic grazing systems for optimal veld utilization and sustainable pasture management.',
      image: 'https://readdy.ai/api/search-image?query=Sustainable%20grazing%20systems%2C%20pasture%20management%2C%20livestock%20grazing%2C%20agricultural%20land%20management%2C%20green%20fields%2C%20cattle%20farming%2C%20sustainable%20agriculture%20practices&width=400&height=300&seq=project3&orientation=landscape',
      technologies: ['Pasture Management', 'Sustainable Grazing', 'Land Optimization'],
      impact: 'Improved veld utilization by 50%'
    },
    {
      title: 'Pruning & Training Program',
      description: 'Implemented systematic pruning and training techniques to improve plant growth and reduce pest infestations.',
      image: 'https://readdy.ai/api/search-image?query=Agricultural%20pruning%20and%20plant%20training%2C%20crop%20management%2C%20sustainable%20farming%20techniques%2C%20plant%20growth%20optimization%2C%20agricultural%20practices%2C%20farm%20maintenance&width=400&height=300&seq=project4&orientation=landscape',
      technologies: ['Plant Science', 'Pest Management', 'Growth Optimization'],
      impact: 'Reduced pest issues by 60%'
    },
    {
      title: 'Terracing Cropping Initiative',
      description: 'Developed terracing systems for cropping to significantly reduce soil erosion and improve water conservation.',
      image: 'https://readdy.ai/api/search-image?query=Agricultural%20terracing%20systems%2C%20soil%20erosion%20prevention%2C%20sustainable%20farming%2C%20water%20conservation%2C%20terraced%20fields%2C%20agricultural%20engineering%2C%20environmental%20protection&width=400&height=300&seq=project5&orientation=landscape',
      technologies: ['Soil Conservation', 'Water Management', 'Sustainable Design'],
      impact: 'Reduced soil erosion by 70%'
    },
    {
      title: 'Efficient Irrigation Management',
      description: 'Designed and managed efficient irrigation systems to optimize water usage and improve crop yields.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20irrigation%20systems%2C%20water%20management%2C%20agricultural%20technology%2C%20efficient%20farming%2C%20crop%20irrigation%2C%20sustainable%20water%20use%2C%20agricultural%20engineering&width=400&height=300&seq=project6&orientation=landscape',
      technologies: ['Irrigation Technology', 'Water Conservation', 'System Management'],
      impact: 'Improved water efficiency by 45%'
    }
  ];

  const references = [
    {
      name: 'Rashida Essa',
      position: 'Chairlady',
      company: 'Pietermaritzburg Muslim Women\'s Organisation',
      phone: '+27 83 278 6030',
      email: null,
      relationship: 'Direct Supervisor during Administrative Secretary position',
      icon: 'ri-user-star-line'
    },
    {
      name: 'Lindokuhle Masuku',
      position: 'Supervisor',
      company: 'Department of Agriculture',
      phone: '+27 73 999 6134',
      alternatePhone: '+27 82 350 9045',
      email: null,
      relationship: 'Direct Supervisor during Agricultural Assistant Practitioner role',
      icon: 'ri-plant-line'
    },
    {
      name: 'Xolani Hlatshwayo',
      position: 'Supervisor',
      company: 'Blind and Deaf Society—Hydroponic Farming',
      phone: '+27 72 957 5326',
      email: null,
      relationship: 'Supervisor during Hydroponic Farming project',
      icon: 'ri-seedling-line'
    }
  ];

  return (
    <section id="projects" ref={sectionRef} className="py-20 bg-white relative z-20 min-h-screen">
      <div className="max-w-6xl mx-auto px-6">
        <div className="transform transition-all duration-1000 translate-y-0 opacity-100">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-serif">
              Projects & Achievements
            </h2>
            <div className="w-24 h-1 bg-green-600 mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Innovative agricultural projects that demonstrate practical application of sustainable farming principles and modern agricultural techniques.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {projects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 cursor-pointer border border-gray-200"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-48 object-cover transform hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center text-green-600 font-semibold text-sm">
                    <i className="ri-trophy-line mr-2"></i>
                    {project.impact}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Professional References Section */}
          <div className="mt-20 bg-gray-50 py-16 px-8 rounded-2xl">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-serif">
                Professional References
              </h3>
              <div className="w-20 h-1 bg-green-600 mx-auto mb-4"></div>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Trusted professionals who can speak to my work ethic, skills, and contributions in agricultural and administrative roles.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {references.map((reference, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-200"
                >
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg ${
                      index === 0 ? 'bg-gradient-to-br from-blue-400 to-blue-600' :
                      index === 1 ? 'bg-gradient-to-br from-green-400 to-green-600' :
                      'bg-gradient-to-br from-purple-400 to-purple-600'
                    }`}>
                      <i className={`${reference.icon} text-2xl text-white`}></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-800 mb-1">{reference.name}</h4>
                    <p className="text-sm font-semibold text-gray-600 mb-1">{reference.position}</p>
                    <p className="text-sm text-gray-500 mb-3">{reference.company}</p>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex items-center text-gray-600">
                      <i className="ri-phone-line mr-3 text-green-600"></i>
                      <a href={`tel:${reference.phone.replace(/\s/g, '')}`} className="hover:text-green-600 transition-colors duration-300">
                        {reference.phone}
                      </a>
                    </div>
                    {reference.alternatePhone && (
                      <div className="flex items-center text-gray-600">
                        <i className="ri-phone-line mr-3 text-green-600"></i>
                        <a href={`tel:${reference.alternatePhone.replace(/\s/g, '')}`} className="hover:text-green-600 transition-colors duration-300">
                          {reference.alternatePhone}
                        </a>
                      </div>
                    )}
                    {reference.email && (
                      <div className="flex items-center text-gray-600">
                        <i className="ri-mail-line mr-3 text-green-600"></i>
                        <a href={`mailto:${reference.email}`} className="break-all hover:text-green-600 transition-colors duration-300">
                          {reference.email}
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 italic leading-relaxed">
                      {reference.relationship}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <div className="bg-green-50 p-8 rounded-lg border border-green-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Ready to Collaborate on Your Next Agricultural Project?
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Let's work together to implement sustainable farming solutions that drive productivity and environmental stewardship.
                </p>
                <a
                  href="https://wa.me/27782333696"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-700 transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-message-line mr-2"></i>
                  Start a Conversation
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
